import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { ShieldCheck, CircleCheck, CircleAlert } from 'lucide-react';
import { companyCommentService, homeService, setAuthToken } from '../services/apiService';
import { usePermissions } from '../context/PermissionsContext';
import { usePendingApprovals } from '../context/PendingApprovalsContext';
import HomeSearch from '../components/HomeSearch';
import StackedTable from '../components/StackedTable';
import { StatCard } from '../components/insights/InsightPage';

const RECENT_NOTES_COUNT = 5;

const APPROVAL_TYPES = [
  { key: 'signup', singular: 'sign-up domain', plural: 'sign-up domains' },
  { key: 'locked', singular: 'locked domain', plural: 'locked domains' },
  { key: 'ipAddresses', singular: 'check-in IP address', plural: 'check-in IP addresses' },
];

// "1 sign-up domain and 2 check-in IP addresses", leaving out the types with nothing waiting
const describePending = (counts) => {
  const parts = APPROVAL_TYPES
    .filter(({ key }) => counts[key] > 0)
    .map(({ key, singular, plural }) => `${counts[key]} ${counts[key] === 1 ? singular : plural}`);
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return isNaN(date.getTime())
    ? dateString
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// A full card only when something is waiting; otherwise one quiet line, so an empty queue takes no room
const NeedsAttention = () => {
  const { counts, total, error } = usePendingApprovals();

  if (total > 0) {
    return (
      <Card className="shadow-sm mb-4">
        <Card.Body className="d-flex flex-column flex-md-row align-items-md-center gap-3">
          <div className="flex-grow-1">
            <h5 className="mb-1 d-flex align-items-center gap-2">
              <ShieldCheck size={16} className="text-muted" /> Approvals
              <Badge bg="warning" pill>{total}</Badge>
            </h5>
            <p className="text-muted mb-0">{describePending(counts)} waiting for a decision</p>
          </div>
          <Button as={Link} to="/approvals" variant="primary" className="align-self-start align-self-md-center">
            Review Approvals
          </Button>
        </Card.Body>
      </Card>
    );
  }

  let icon = <Spinner animation="border" size="sm" />;
  let message = 'Checking for requests waiting for a decision';
  if (counts) {
    icon = <CircleCheck size={16} />;
    message = 'Nothing needs attention';
  } else if (error) {
    icon = <CircleAlert size={16} />;
    message = 'Failed to load pending approvals. Refresh the page to try again.';
  }
  return <p className="text-muted d-flex align-items-center gap-2 mb-4">{icon} {message}</p>;
};

const Home = () => {
  const { instance, accounts } = useMsal();
  const { canEdit } = usePermissions();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(false);
  const [notes, setNotes] = useState(null);
  const [notesError, setNotesError] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await instance.acquireTokenSilent({
          scopes: ['api://7e9ba977-33b0-4e9c-ae75-55f65da317bd/access'],
          account: accounts[0],
        });
        setAuthToken(response.accessToken);
      } catch (err) {
        console.error('Failed to get a token for the home page', err);
        setSummaryError(true);
        setNotesError(true);
        return;
      }

      // Settled separately, so one failing leaves the other on screen
      const [summaryResult, notesResult] = await Promise.allSettled([
        homeService.getSummary(),
        companyCommentService.getRecentComments(),
      ]);
      if (summaryResult.status === 'fulfilled') {
        setSummary(summaryResult.value.data);
      } else {
        console.error('Failed to load the home page figures', summaryResult.reason);
        setSummaryError(true);
      }
      if (notesResult.status === 'fulfilled') {
        setNotes(notesResult.value.data.slice(0, RECENT_NOTES_COUNT));
      } else {
        console.error('Failed to load recent company notes', notesResult.reason);
        setNotesError(true);
      }
    };
    if (accounts[0]) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts[0]?.homeAccountId]);

  const figure = (value) => (summary ? value.toLocaleString() : <Spinner animation="border" size="sm" />);

  return (
    <Container className="container-main">
      <Row className="mb-4">
        <Col>
          <h1>Welcome{accounts[0]?.idTokenClaims?.name ? `, ${accounts[0].idTokenClaims.name.split(' ')[0]}` : ''}</h1>
        </Col>
      </Row>

      <NeedsAttention />

      <HomeSearch />

      {summaryError ? (
        <p className="text-muted d-flex align-items-center gap-2 mb-4">
          <CircleAlert size={16} /> Failed to load the figures. Refresh the page to try again.
        </p>
      ) : (
        <Row className="gx-3 gx-md-4 mb-2">
          <StatCard label="Total users" value={figure(summary?.totalUsers)} />
          <StatCard label="Paid users" value={figure(summary?.paidUsers)} />
          <StatCard label="New companies" value={figure(summary?.companiesSignedUp)} sub="Last 7 days" />
          <StatCard label="New users" value={figure(summary?.usersSignedUp)} sub="Last 7 days" />
        </Row>
      )}

      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-light d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Recent notes</h5>
          <Link to="/recent-notes" className="small">View all</Link>
        </Card.Header>
        <Card.Body className="p-0">
          {notesError ? (
            <div className="p-4 text-center text-muted">Failed to load recent notes. Refresh the page to try again.</div>
          ) : notes === null ? (
            <div className="p-4 text-center">
              <Spinner animation="border" size="sm" role="status">
                <span className="visually-hidden">Loading recent notes...</span>
              </Spinner>
            </div>
          ) : notes.length === 0 ? (
            <div className="p-4 text-center text-muted">No company notes have been added yet</div>
          ) : (
            <StackedTable responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Company</th>
                  <th>Note</th>
                  <th className="d-none d-md-table-cell">Added By</th>
                  <th className="text-nowrap">Added</th>
                </tr>
              </thead>
              <tbody>
                {notes.map((note) => (
                  <tr
                    key={note.id}
                    onClick={() => navigate(`/crm/companies/${note.companyId}`)}
                    style={{ cursor: 'pointer' }}
                    className="table-row-hover"
                  >
                    <td className="align-middle home-notes-company"><strong>{note.companyName || note.companyId}</strong></td>
                    <td className="align-middle stack-block"><span className="home-note-text">{note.commentText}</span></td>
                    <td className="align-middle d-none d-md-table-cell text-muted text-nowrap">
                      {note.createdByName || note.createdByEmail || 'Unknown'}
                    </td>
                    <td className="align-middle text-nowrap">{formatDate(note.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </StackedTable>
          )}
        </Card.Body>
      </Card>

      {canEdit && (
        <nav className="home-tools mb-4" aria-label="Tools">
          <h6 className="mb-0">Tools</h6>
          <Link to="/cache-controls">Cache controls</Link>
          <Link to="/data-insights">Data insights</Link>
          <Link to="/logs">Logs</Link>
          <Link to="/server-health">Server health</Link>
          <Link to="/marketing-site">Marketing site</Link>
        </nav>
      )}
    </Container>
  );
};

export default Home;
